import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Form, Formik } from "formik";
import { Button, TextBox, Overlay } from "../../..";
import ComboBox from "../../../common/ComboBox";
import FileUploader from "../../../common/FileUploader";
import useCertificates from "../../../../hooks/useCertificates";
import ProfileContext from "../../ProfileContext";
import { PAGE_STATE, langComponent } from "../../../../constants";
import AppContext from "../../../../AppContext";

// * Images
import { ReactComponent as ArrowRightSvg } from "../../../../assets/icons/arrow-right.svg";
import View from "./View";
import { toast } from "react-toastify";
import { format } from "date-fns";
import { isValidNumber } from "../../../../helpers/common.helper";
import _ from "lodash";

const monthOptions = _.range(1, 13).map((_, month) => ({
  name: format(new Date(1900, month, 1), "MMMM"),
  value: (month + 1).toString(),
}));

const Modal = ({ mode, id, onClose, onSuccess }) => {
  const {
    createCertificate,
    getCertificate,
    patchCertificate,
    uploadCertificate,
  } = useCertificates();
  const { user, company, isCompany } = useContext(ProfileContext);
  const { getLang } = useContext(AppContext);
  const Translation = getLang(langComponent.certificate);
  const TranslationCommon = getLang(langComponent.common);
  const [pageState, setPageState] = useState(
    mode === "add" ? PAGE_STATE.IDLE : PAGE_STATE.LOADING
  );
  const [savingState, setSavingState] = useState(PAGE_STATE.IDLE);
  const [data, setData] = useState(undefined);
  const [certFile, setCertFile] = useState(undefined);

  useEffect(() => {
    if (mode === "add") return;
    getCertificateData();
  }, []);

  const getCertificateData = async () => {
    const certificate = await getCertificate(id);
    setData(certificate);
    setPageState(PAGE_STATE.IDLE);
  };

  // * Handle submit
  const handleSubmit = async (values) => {
    const { name, issuer, month, year, url } = values;
    if (mode === "add") {
      handleCreate({
        name,
        issuer,
        effectiveDate: new Date(year, +month - 1, 1),
        url,
      });
    }
    if (mode === "edit") {
      await handleEdit({
        name,
        issuer,
        effectiveDate: new Date(year, +month - 1, 1),
        url,
      });
      onSuccess();
    }
  };

  const handleCreate = async (body) => {
    try {
      setSavingState(PAGE_STATE.LOADING);
      const cert = await createCertificate({
        ...body,
        user: !isCompany ? user?._id : undefined,
        company: isCompany ? company?._id : undefined,
        status: 1,
      });

      if (certFile) await uploadCertificate(cert._id, certFile);
      onSuccess();
    } catch (error) {
      toast.error("Unable to create certificate");
      setSavingState(PAGE_STATE.IDLE);
    }
  };

  const handleEdit = async (body) => {
    try {
      setSavingState(PAGE_STATE.LOADING);

      await patchCertificate(id, {
        ...body,
        status: 1,
      });

      onSuccess();
    } catch (error) {
      toast.error("Unable to update certificate");
      setSavingState(PAGE_STATE.IDLE);
    }
  };

  const handleValidate = ({ name, issuer, month, year, url }) => {
    let errors = {};
    if (!name) errors.name = Translation("Name cannot be blank");
    if (!issuer)
      errors.issuer = Translation("Organization Issuer cannot be blank");
    if (!month) errors.month = Translation("Please specify a month");
    if (!year || year > 9999 || year < 0 || !isValidNumber(year))
      errors.year = Translation("Please specify a year");

    return errors;
  };

  const handleFileChange = (files) => {
    setCertFile(files[0]);
  };

  if (
    (mode === "edit" || mode === "view") &&
    (pageState === PAGE_STATE.LOADING || !data)
  ) {
    return <Overlay onClose={onClose} content={<>Loading</>} />;
  }

  if (mode === "view") {
    return <View cert={data} onClose={onClose} />;
  }

  return (
    <Overlay
      onClose={onClose}
      content={
        <div className="profile-card-certs-modal">
          <Formik
            initialValues={{
              name: mode === "edit" ? data.name : "",
              issuer: mode === "edit" ? data.issuer : "",
              month:
                mode === "edit"
                  ? (new Date(data.effectiveDate).getMonth() + 1).toString()
                  : "",
              year:
                mode === "edit"
                  ? new Date(data.effectiveDate).getFullYear()
                  : "",
              url: mode === "edit" ? data.url : "",
            }}
            validate={handleValidate}
            onSubmit={handleSubmit}
          >
            <Form className="profile-card-certs-modal-form">
              <h1 className="profile-card-certs-modal-form__header">
                {mode === "edit"
                  ? Translation("Edit Certificate")
                  : Translation("Add Certificate")}
              </h1>
              <TextBox
                name="name"
                placeholder={Translation(
                  "Name of certification Ex. Urbann Certification"
                )}
                style={{ gridColumn: "1 / 3" }}
              />
              <TextBox
                name="issuer"
                placeholder={Translation("Issuing organization Ex. Urbann")}
                style={{ gridColumn: "1 / 3" }}
              />
              <ComboBox
                placeholder={TranslationCommon("Select month")}
                name="month"
                options={monthOptions}
              />
              <TextBox
                name="year"
                placeholder={TranslationCommon("Year")}
                type="number"
              />
              <TextBox
                name="url"
                placeholder={Translation("Certification URL (optional)")}
                type="text"
                style={{ gridColumn: "1 / 3" }}
              />
              <div className="profile-card-certs-modal-form-upload">
                <FileUploader
                  id="certificate"
                  label={
                    !certFile
                      ? Translation("Upload certificate")
                      : Translation("Change certificate")
                  }
                  onChange={handleFileChange}
                />
                {certFile && (
                  <span className="profile-card-certs-modal-form-upload__file">
                    {certFile.name}
                  </span>
                )}
              </div>
              {/* <div className="profile-card-certs-modal-form-skills">
                <Button label="Add Skill" leadingIcon={<ArrowRightSvg />} />
              </div> */}
              <div className="profile-card-certs-modal-form__footer">
                {savingState === PAGE_STATE.IDLE && (
                  <Button
                    type="submit"
                    label={TranslationCommon("Save")}
                    leadingIcon={<ArrowRightSvg />}
                  />
                )}
              </div>
            </Form>
          </Formik>
        </div>
      }
    />
  );
};

Modal.propTypes = {
  mode: PropTypes.string.isRequired,
  id: PropTypes.string,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
};

export default Modal;
